import React, { useRef, useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, ChevronRight, FileText, Link2, TrendingDown, TrendingUp, Unplug } from 'lucide-react';
import { NotificationFeedItem } from '../../../api/types';
import notificationApi from '../../../api/notificationApi';
import { useWorkspace } from '../../../context/WorkspaceContext';
import WeeklySummaryModal from '../../signals/components/WeeklySummaryModal';

type FilterKey = 'all' | 'unread' | 'price_alert' | 'weekly_summary' | 'connection_event';

type DisplayNotification = NotificationFeedItem & {
    actionLabel: string;
    onAction: () => void | Promise<void>;
};

const formatDateTime = (value?: string | null) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        + ' · '
        + parsed.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const iconForKind = (kind: string) => {
    if (kind === 'price_increase') return <TrendingUp className="h-4 w-4 text-red-600" />;
    if (kind === 'price_decrease') return <TrendingDown className="h-4 w-4 text-emerald-600" />;
    if (kind === 'weekly_summary') return <FileText className="h-4 w-4 text-indigo-600" />;
    if (kind === 'qbo_connected' || kind === 'bank_account_connected') return <Link2 className="h-4 w-4 text-emerald-600" />;
    if (kind === 'qbo_disconnected' || kind === 'bank_account_disconnected') return <Unplug className="h-4 w-4 text-amber-600" />;
    return <Bell className="h-4 w-4 text-gray-500" />;
};

const itemMatchesFilter = (item: NotificationFeedItem, filter: FilterKey) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !item.is_read;
    if (filter === 'price_alert') return item.kind === 'price_increase' || item.kind === 'price_decrease';
    if (filter === 'weekly_summary') return item.kind === 'weekly_summary';
    if (filter === 'connection_event') return item.kind.startsWith('qbo_') || item.kind.startsWith('bank_account_') || item.kind === 'connection_event';
    return true;
};

export default function Notification() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { activeBusinessId } = useWorkspace();
    const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
    const [showWeeklySummary, setShowWeeklySummary] = useState(false);
    const filterScrollerRef = useRef<HTMLDivElement | null>(null);
    const dragStateRef = useRef({ active: false, startX: 0, scrollLeft: 0, dragged: false });

    const queryKind = activeFilter === 'price_alert' || activeFilter === 'weekly_summary' || activeFilter === 'connection_event' ? activeFilter : undefined;
    const feedQueryKey = ['notification-feed', activeBusinessId, queryKind];
    const { data: feed, isLoading, isError } = useQuery({
        queryKey: feedQueryKey,
        queryFn: () => notificationApi.getFeed(activeBusinessId || undefined, queryKind),
        retry: false,
    });

    const markReadMutation = useMutation({
        mutationFn: ({ key, clicked }: { key: string; clicked?: boolean }) =>
            notificationApi.markRead(key, activeBusinessId || undefined, clicked ?? true),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notification-feed'] });
        },
    });

    const markAllReadMutation = useMutation({
        mutationFn: () => notificationApi.markAllRead(activeBusinessId || undefined),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notification-feed'] });
        },
    });

    const feedItems = useMemo<DisplayNotification[]>(() => {
        return (feed?.items || []).map((item) => ({
            ...item,
            actionLabel: item.kind === 'weekly_summary' ? 'Open summary' : item.kind.startsWith('price_') ? 'View inventory' : item.kind.startsWith('qbo_') || item.kind.startsWith('bank_account_') ? 'View connectors' : 'Open',
            onAction: async () => {
                if (!item.is_read) {
                    await markReadMutation.mutateAsync({ key: item.key, clicked: true });
                }
                if (item.kind === 'weekly_summary') {
                    setShowWeeklySummary(true);
                    return;
                }
                if (item.target_url) {
                    navigate(item.target_url);
                }
            },
        }));
    }, [feed?.items, markReadMutation, navigate]);

    const displayItems = useMemo(() => {
        const base = feedItems.filter((item) => itemMatchesFilter(item, activeFilter));
        return [...base].sort((left, right) => String(right.created_at || '').localeCompare(String(left.created_at || '')));
    }, [activeFilter, feedItems]);

    const unreadCount = feed?.unread_count || 0;
    const filterOptions: Array<{ key: FilterKey; label: string }> = [
        { key: 'all', label: 'All' },
        { key: 'unread', label: 'Unread' },
        { key: 'price_alert', label: 'Price Alerts' },
        { key: 'weekly_summary', label: 'Weekly Summary' },
        { key: 'connection_event', label: 'Connections' },
    ];

    const handleFilterPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse') return;
        const scroller = filterScrollerRef.current;
        if (!scroller) return;
        dragStateRef.current = {
            active: true,
            startX: event.clientX,
            scrollLeft: scroller.scrollLeft,
            dragged: false,
        };
        scroller.setPointerCapture?.(event.pointerId);
    };

    const handleFilterPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse') return;
        const scroller = filterScrollerRef.current;
        const drag = dragStateRef.current;
        if (!scroller || !drag.active) return;
        const delta = event.clientX - drag.startX;
        if (Math.abs(delta) > 8) {
            drag.dragged = true;
        }
        scroller.scrollLeft = drag.scrollLeft - delta;
    };

    const handleFilterPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse') return;
        const scroller = filterScrollerRef.current;
        if (scroller?.hasPointerCapture?.(event.pointerId)) {
            scroller.releasePointerCapture(event.pointerId);
        }
        window.setTimeout(() => {
            dragStateRef.current = { active: false, startX: 0, scrollLeft: 0, dragged: false };
        }, 0);
    };

    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>, key: FilterKey) => {
        if (dragStateRef.current.dragged) {
            event.preventDefault();
            return;
        }
        setActiveFilter(key);
    };

    return (
        <div className="w-full flex flex-col gap-3">
            <div className="flex min-w-0 items-center gap-2">
                <div
                    ref={filterScrollerRef}
                    onPointerDown={handleFilterPointerDown}
                    onPointerMove={handleFilterPointerMove}
                    onPointerUp={handleFilterPointerUp}
                    onPointerCancel={handleFilterPointerUp}
                    className="min-w-0 flex-1 cursor-grab touch-pan-x select-none overflow-x-auto active:cursor-grabbing [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                    <div className="flex w-max items-center gap-2">
                    {filterOptions.map((option) => (
                        <button
                            key={option.key}
                            type="button"
                            onClick={(event) => handleFilterClick(event, option.key)}
                            className={`box-border inline-flex h-8 max-h-8 min-h-8 shrink-0 appearance-none items-center justify-center rounded-md border border-solid px-3 text-sm font-medium leading-[14px] outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${activeFilter === option.key
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            {option.label}
                        </button>
                    ))}
                    </div>
                </div>
                <button
                    type="button"
                    disabled={unreadCount === 0 || markAllReadMutation.isPending}
                    onClick={() => markAllReadMutation.mutate()}
                    className="box-border inline-flex h-8 max-h-8 min-h-8 shrink-0 appearance-none items-center justify-center gap-2 rounded-md border border-solid border-gray-300 bg-white px-3 text-sm font-medium leading-[14px] text-gray-700 shadow-sm outline-none ring-0 hover:bg-gray-50 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 max-[420px]:px-2"
                >
                    <Check className="h-3.5 w-3.5 shrink-0" />
                    <span className="hidden whitespace-nowrap sm:inline">Mark all as read</span>
                    <span className="whitespace-nowrap sm:hidden">Read all</span>
                </button>
            </div>

            <div className="w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                {isLoading ? (
                    <div className="p-4 text-sm text-gray-500">Loading notifications...</div>
                ) : isError ? (
                    <div className="p-4 text-sm text-red-600">Failed to load notifications.</div>
                ) : displayItems.length === 0 ? (
                    <div className="p-4">
                        <p className="font-sans text-sm text-gray-600">You have no notifications.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {displayItems.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                onClick={() => void item.onAction()}
                                className="flex w-full flex-col items-start justify-between gap-3 p-4 text-left transition-colors hover:bg-white sm:flex-row sm:items-center"
                            >
                                <span className="flex min-w-0 items-start gap-3">
                                    <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${item.is_read ? 'bg-gray-300' : 'bg-red-500'}`} />
                                    <span className="min-w-0">
                                        <span className="flex min-w-0 items-center gap-2">
                                            {iconForKind(item.kind)}
                                            <span className="truncate font-sans text-sm font-semibold text-gray-900">{item.title}</span>
                                        </span>
                                        <span className="mt-1 block font-sans text-sm text-gray-600">{item.description}</span>
                                        <span className="mt-0.5 block font-sans text-xs text-gray-500">{formatDateTime(item.created_at)}</span>
                                    </span>
                                </span>
                                <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                                    {item.actionLabel}
                                    <ChevronRight className="h-4 w-4" />
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <WeeklySummaryModal
                open={showWeeklySummary}
                businessId={activeBusinessId || undefined}
                onClose={() => setShowWeeklySummary(false)}
            />
        </div>
    );
}
